import { z } from 'zod';

/**
 * Every value here ships in the bundle and is public. That is correct for a
 * public client: the Firebase web API key identifies the project rather than
 * authenticating anything — Firebase's security model relies on backend
 * verification, not on hiding it. Nothing that must stay private may be added
 * to this schema.
 */
const schema = z.object({
  VITE_FIREBASE_API_KEY: z.string().min(1),
  VITE_FIREBASE_AUTH_DOMAIN: z.string().min(1),
  VITE_FIREBASE_PROJECT_ID: z.string().min(1),
  // Dev/CI only. env.ts refuses this in a production build below.
  VITE_FIREBASE_AUTH_EMULATOR_URL: z.string().url().optional(),
  VITE_API_BASE_URL: z.string().url(),
  VITE_API_MODE: z.enum(['live', 'mock']).default('live'),
  VITE_AUTH_MODE: z.enum(['firebase', 'fake']).default('firebase'),
  // Fake auth only: which second factors the stub account starts with. See
  // src/auth/fakeAuthProvider.ts.
  VITE_FAKE_AUTH_MFA: z.enum(['none', 'totp', 'sms']).default('none'),
  VITE_ALLOW_DEMO_BUILD: z.enum(['true', 'false']).default('false'),
});

function read() {
  const parsed = schema.safeParse(import.meta.env);
  if (!parsed.success) {
    // Failing at module load with the offending keys named beats failing later
    // inside Firebase, where a missing apiKey surfaces as an opaque auth/*
    // error.
    const issues = parsed.error.issues.map((i) => `  ${i.path.join('.')}: ${i.message}`).join('\n');
    throw new Error(`Invalid frontend configuration.\n${issues}\n\nSee .env.example.`);
  }

  const values = parsed.data;

  // The stub-auth and mock-API escape hatches let the shell be built and
  // demoed without a Firebase project. They must never reach a real
  // production release by accident, so the guard is build-time and requires a
  // second, explicit opt-in (VITE_ALLOW_DEMO_BUILD=true) to bypass — for a
  // no-backend demo deploy only, never for an actual release.
  const demoBuild = import.meta.env.PROD && values.VITE_ALLOW_DEMO_BUILD === 'true';
  if (import.meta.env.PROD && values.VITE_AUTH_MODE === 'fake' && !demoBuild) {
    throw new Error('VITE_AUTH_MODE=fake is not permitted in a production build.');
  }
  if (import.meta.env.PROD && values.VITE_API_MODE === 'mock' && !demoBuild) {
    throw new Error('VITE_API_MODE=mock is not permitted in a production build.');
  }
  if (import.meta.env.PROD && values.VITE_FIREBASE_AUTH_EMULATOR_URL) {
    throw new Error('VITE_FIREBASE_AUTH_EMULATOR_URL must not be set in a production build.');
  }

  return values;
}

export const env = read();

export const useFakeAuth = env.VITE_AUTH_MODE === 'fake';
export const useMockApi = env.VITE_API_MODE === 'mock';
